import { Collapse, CollapseProps, Flex, Typography } from 'antd';
import { useMemo, useState } from 'react';

import { Loader } from '@components';
import {
  CompanyAccountingEdit,
  CompanyBobIdEdit,
  CompanyContactPersonEdit,
  CompanyDetailsFeatures,
  CompanyDetailsFeaturesEdit,
  CompanyDetailsInfo,
  CompanyDetailsPerson,
  CompanyDetailsServiceInfo,
  CompanyDetailsServiceInfoEdit,
} from '@entities';
import { useCurrentCompany } from '@hooks';

const { Text } = Typography;

const CompanyInfo = () => {
  const {
    data: company,
    isFetching: isFetchingCompany,
    refetch: refetchCompany,
  } = useCurrentCompany();

  // Three local forms are left on this tab, and each one edits something MDS has no column for:
  // the BOB50 dossier, the accounting block and the contact person. Everything else is read-only
  // and changed in Compliance, which forwards its writes to MDS.
  const [modal, setModal] = useState<
    'bobId' | 'accounting' | 'contact' | 'features' | 'service' | null
  >(null);

  const handleAfterEdit = () => {
    setModal(null);
    refetchCompany();
  };

  const handleCloseModal = () => {
    setModal(null);
  };

  const sections: CollapseProps['items'] = useMemo(
    () =>
      company && [
        {
          key: 'details',
          label: <Text strong>Company details</Text>,
          children: (
            <CompanyDetailsInfo
              id={company.id}
              name={company.name}
              referenceId={company.referenceId}
              bobReferenceId={company.bobReferenceId}
              details={company.details}
              status={company.status}
              onEditBobId={() => setModal('bobId')}
              onEditAccounting={() => setModal('accounting')}
            />
          ),
        },
        {
          key: 'contact',
          label: <Text strong>Contact person</Text>,
          children: (
            <CompanyDetailsPerson
              details={company.details?.contactPerson}
              onEdit={() => setModal('contact')}
            />
          ),
        },
        {
          key: 'service',
          label: <Text strong>Service</Text>,
          children: (
            <CompanyDetailsServiceInfo
              company={company}
              onEdit={() => setModal('service')}
            />
          ),
        },
        {
          key: 'features',
          label: <Text strong>Features</Text>,
          children: (
            <CompanyDetailsFeatures
              details={company.featuresStatus}
              onEdit={() => setModal('features')}
            />
          ),
        },
      ],
    [company]
  );

  return (
    <>
      <Flex gap={16} vertical>
        {isFetchingCompany ? (
          <Loader />
        ) : company ? (
          <Collapse
            items={sections}
            defaultActiveKey={[
              'details',
              'contact',
              'service',
              'incorporation',
            ]}
          />
        ) : null}
      </Flex>
      {company && (
        <>
          <CompanyBobIdEdit
            company={company}
            open={modal === 'bobId'}
            onCancel={handleCloseModal}
            onAfterEdit={handleAfterEdit}
          />

          <CompanyAccountingEdit
            company={company}
            open={modal === 'accounting'}
            onCancel={handleCloseModal}
            onAfterEdit={handleAfterEdit}
          />

          <CompanyContactPersonEdit
            company={company}
            open={modal === 'contact'}
            onCancel={handleCloseModal}
            onAfterEdit={handleAfterEdit}
          />

          <CompanyDetailsFeaturesEdit
            featuresStatus={company.featuresStatus}
            open={modal == 'features'}
            onCancel={handleCloseModal}
            onAfterEdit={handleAfterEdit}
          />

          <CompanyDetailsServiceInfoEdit
            company={company}
            open={modal === 'service'}
            onCancel={handleCloseModal}
            onAfterEdit={handleAfterEdit}
          />
        </>
      )}
    </>
  );
};

export default CompanyInfo;
